import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { InfoIconButton } from './InfoIconButton';

interface PageInfoTooltipProps {
  text: string;
  testIdPrefix: string;
}

/**
 * Popover-style tooltip triggered by clicking an info icon.
 * Closes on outside click. Renders via portal to escape overflow clipping.
 * Positions itself to stay within the viewport on mobile.
 */
export function PageInfoTooltip({ text, testIdPrefix }: PageInfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideTrigger = containerRef.current?.contains(target);
      const insideTooltip = tooltipRef.current?.contains(target);
      if (!insideTrigger && !insideTooltip) {
        setOpen(false);
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', close);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', close);
    };
  }, [open]);

  /** Ancestors of `trigger`, stopping well short of `<body>` -- the search
   * space `narrowestAncestorRight` looks for a bounding column within. */
  function collectAncestors(trigger: HTMLElement): HTMLElement[] {
    const ancestors: HTMLElement[] = [];
    let node: HTMLElement | null = trigger.parentElement;
    let depth = 0;
    while (node && node !== document.body && depth < 8) {
      ancestors.push(node);
      node = node.parentElement;
      depth += 1;
    }
    return ancestors;
  }

  /** True for an ancestor narrow enough to be a real layout column (e.g. a
   * fixed-width sidebar), not just the viewport-spanning page body. */
  function isBoundingAncestor(node: HTMLElement): boolean {
    const width = node.getBoundingClientRect().width;
    return width > 0 && width < window.innerWidth * 0.9;
  }

  /** The popover is portalled to `document.body`, so left to itself it only
   * avoids running off the viewport edge -- on a layout with a fixed-width
   * sidebar next to the trigger's own column (e.g. a settings page beside a
   * sticky preview panel), that's not enough: it opens wide enough to cover
   * the sibling content. The narrowest bounding ancestor's real right edge
   * clamps it to that column instead, and is a no-op -- same as the
   * viewport-only clamp -- on a plain full-width layout. */
  function narrowestAncestorRight(trigger: HTMLElement): number {
    const rights = collectAncestors(trigger)
      .filter(isBoundingAncestor)
      .map((node) => node.getBoundingClientRect().right);
    return Math.min(window.innerWidth, ...rights);
  }

  const adjustPosition = useCallback((el: HTMLDivElement | null) => {
    if (!el || !containerRef.current) return;
    tooltipRef.current = el;
    const trigger = containerRef.current.getBoundingClientRect();
    const margin = 16;
    el.style.top = `${trigger.bottom + 8}px`;
    const idealLeft = trigger.left;
    const containerBound = narrowestAncestorRight(containerRef.current);
    const maxLeft = Math.min(window.innerWidth, containerBound) - el.offsetWidth - margin;
    el.style.left = `${Math.max(margin, Math.min(idealLeft, maxLeft))}px`;
  }, []);

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <InfoIconButton
        onClick={() => setOpen((v) => !v)}
        title=""
        data-testid={`${testIdPrefix}-info-toggle`}
      />
      {open && createPortal(
        <div
          ref={adjustPosition}
          role="tooltip"
          data-testid={`${testIdPrefix}-info-balloon`}
          style={{
            position: 'fixed',
            width: 'min(320px, calc(100vw - 32px))',
            padding: 'var(--space-3) var(--space-4)',
            borderRadius: 'var(--radius-lg)',
            background: 'var(--color-popover)',
            color: 'var(--color-popover-foreground)',
            fontSize: 'var(--text-body-sm)',
            lineHeight: 1.5,
            border: '1px solid var(--border-subtle)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 1000,
          }}
        >
          {text}
        </div>,
        document.body,
      )}
    </div>
  );
}
